import { useState } from 'react'
import {
  CurecallButton,
  CurecallInput,
  CurecallModalContainer,
  ECurecallButtonAppearance,
  ECurecallNotificationAppearance,
} from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useNotification } from 'src/providers/notification.provider'
import { TwoFactorService } from 'src/services/two-factor.service'
import TwoFactorBackupCodes from './two-factor-backup-codes.component'

type Props = {
  isOpen: boolean
  onClose: () => void
  onSetupComplete: () => void
}

type TSetupStep = 'init' | 'confirm' | 'backup-codes'

export default function TwoFactorSetupSms({ isOpen, onClose, onSetupComplete }: Props) {
  const { t } = useTranslation()
  const { notify } = useNotification()

  const [step, setStep] = useState<TSetupStep>('init')
  const [confirmCode, setConfirmCode] = useState('')
  const [backupCodes, setBackupCodes] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSendCode = async () => {
    setIsLoading(true)
    try {
      await TwoFactorService.setupSms()
      setStep('confirm')
    } catch (error: any) {
      notify({
        title: t('common.actions.error'),
        message: error,
        appearance: ECurecallNotificationAppearance.ERROR,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleConfirm = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const result = await TwoFactorService.confirmSms(confirmCode.trim())
      setBackupCodes(result.backupCodes)
      setStep('backup-codes')
    } catch {
      setError(t('pages.auth.two-factor.error.invalid-code'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleAcknowledgeBackupCodes = () => {
    onSetupComplete()
    onClose()
  }

  return (
    <CurecallModalContainer
      closeModalAriaLabel={t('common.actions.close')}
      title={t('pages.auth.account.two-factor.setup-sms-title')}
      isOpen={isOpen}
      toggle={onClose}
      dialogClassName="sm:c-w-[500px]"
    >
      {step === 'init' && (
        <div className="c-flex c-flex-col c-gap-6">
          <p className="c-text-sm c-text-gray-600">
            {t('pages.auth.account.two-factor.sms-setup-description')}
          </p>
          <CurecallButton onClick={handleSendCode} isLoading={isLoading}>
            {t('pages.auth.account.two-factor.enable-sms')}
          </CurecallButton>
        </div>
      )}

      {step === 'confirm' && (
        <div className="c-flex c-flex-col c-gap-6">
          <p className="c-text-sm c-text-gray-600">
            {t('pages.auth.account.two-factor.sms-confirm-description')}
          </p>

          <CurecallInput
            label={t('pages.auth.two-factor.code-label')}
            value={confirmCode}
            onChange={(e) => {
              setConfirmCode(e.target.value)
              setError(null)
            }}
            placeholder="000000"
            errorMessage={error ?? undefined}
            maxLength={6}
            autoFocus
          />

          <div className="c-flex c-justify-center c-gap-4">
            <CurecallButton appearance={ECurecallButtonAppearance.CANCEL} onClick={onClose}>
              {t('common.actions.cancel')}
            </CurecallButton>
            <CurecallButton onClick={handleConfirm} isLoading={isLoading} isDisabled={confirmCode.length < 6}>
              {t('common.actions.confirm')}
            </CurecallButton>
          </div>
        </div>
      )}

      {step === 'backup-codes' && (
        <TwoFactorBackupCodes backupCodes={backupCodes} onAcknowledge={handleAcknowledgeBackupCodes} />
      )}
    </CurecallModalContainer>
  )
}
